<template>
  <v-dialog v-model="show" width="1150" scrollable>
    <v-card v-if="show">
      <v-card-title
        class="text-h6 white--text primary d-flex justify-space-between align-center py-2"
      >
        <span class="text-truncate" style="max-width: 90%">
          {{ name || "Document" }}
        </span>
        <v-btn icon @click="close">
          <v-icon color="white">mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="pdf-viewer-body pa-0">
        <!-- Loading -->
        <div
          v-if="loading"
          class="d-flex flex-column align-center justify-center"
          style="height: 100%"
        >
          <v-progress-circular indeterminate color="primary" size="48" />
          <p class="mt-3 grey--text">Loading PDF…</p>
        </div>

        <!-- Error -->
        <div
          v-else-if="loadError"
          class="d-flex flex-column align-center justify-center text-center"
          style="height: 100%"
        >
          <v-icon color="error" size="48">mdi-alert-circle-outline</v-icon>
          <p class="mt-3 grey--text">Unable to preview this PDF.</p>
          <p v-if="errorDetail" class="grey--text text--darken-1 caption px-6">
            {{ errorDetail }}
          </p>
          <p class="grey--text caption px-6">
            Use “Open in new tab” below to download it instead.
          </p>
        </div>

        <!-- Pages -->
        <div v-else class="pdf-pages">
          <pdf
            v-for="i in numPages"
            :key="i"
            :src="pdfTask"
            :page="i"
            :scale.sync="scale"
            :resize="true"
            class="pdf-page"
          ></pdf>
        </div>
      </v-card-text>

      <v-card-actions class="justify-end py-2">
        <a
          :href="sourceUrl || url"
          target="_blank"
          rel="noopener"
          class="text-decoration-none"
        >
          <v-btn text color="primary">
            <v-icon left small>mdi-open-in-new</v-icon>
            Open in new tab
          </v-btn>
        </a>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import pdfvuer from "pdfvuer";
import { mapState } from "vuex";

export default {
  name: "PdfViewerDialog",
  components: {
    pdf: pdfvuer,
  },
  data() {
    return {
      pdfTask: undefined,
      numPages: 0,
      scale: "page-width",
      loading: false,
      loadError: false,
      errorDetail: "",
      // Guards against a slow load resolving after the dialog has moved on to a
      // different document and overwriting the newer result.
      loadToken: 0,
    };
  },
  computed: {
    ...mapState("pdfViewer", ["url", "name", "sourceUrl"]),
    show: {
      get() {
        return this.$store.state.pdfViewer.show;
      },
      set(value) {
        if (!value) this.close();
      },
    },
  },
  watch: {
    show(value) {
      if (value && this.url) {
        this.loadPdf();
      } else {
        this.reset();
      }
    },
    // Opening a second document without closing the dialog changes the store URL
    // while `show` stays true, so the watcher above never fires and the previous
    // document would keep rendering.
    url(value) {
      if (this.show && value) this.loadPdf();
    },
  },
  methods: {
    loadPdf() {
      this.reset();
      this.loading = true;
      const token = ++this.loadToken;
      try {
        this.pdfTask = pdfvuer.createLoadingTask(this.url);
        this.pdfTask
          .then((pdf) => {
            if (token !== this.loadToken) return;
            this.numPages = pdf.numPages;
            this.loading = false;
          })
          .catch((err) => {
            if (token !== this.loadToken) return;
            this.failLoad(err);
          });
      } catch (err) {
        this.failLoad(err);
      }
    },
    // A CORS block, a 404 and a corrupt file all used to read "Unable to preview
    // this PDF"; keep the pdf.js error name/message so the cause is visible.
    failLoad(err) {
      console.error("PDF load error:", err);
      this.loading = false;
      this.loadError = true;
      const name = (err && err.name) || "";
      const message = (err && err.message) || "";
      this.errorDetail = [name, message].filter(Boolean).join(": ");
    },
    reset() {
      this.pdfTask = undefined;
      this.numPages = 0;
      this.loading = false;
      this.loadError = false;
      this.errorDetail = "";
    },
    close() {
      this.$store.dispatch("pdfViewer/close");
    },
  },
};
</script>

<style scoped>
.pdf-viewer-body {
  height: 85vh;
  background: #f6f7fa;
}
.pdf-pages {
  padding: 16px;
}
.pdf-page {
  width: 100%;
  max-width: 900px;
  margin: 0 auto 16px auto;
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.15);
}
</style>
